import React, { useMemo } from "react";
import { usePanelData } from "../usePanelData";
import SeriesChart, { type SeriesDef } from "./SeriesChart";
import type { DataBinding, ResolvedRange } from "../spec";

interface TimeSeriesPanelProps {
  title?: string;
  binding: DataBinding;
  range: ResolvedRange;
  machineBK?: string;
  height?: number;
}

/**
 * Multi-channel time-series panel: one `usePanelData` subscription feeding a
 * shared {@link SeriesChart} (all charting/zoom/hover/full-resolution logic).
 */
const TimeSeriesPanel: React.FC<TimeSeriesPanelProps> = ({
  title,
  binding,
  range,
  machineBK,
  height = 320,
}) => {
  const { series, loading, loadDetail } = usePanelData(binding, range);

  const seriesDefs = useMemo<SeriesDef[]>(
    () =>
      binding.channels.map((c) => ({
        key: c.key,
        name: c.label ?? c.key,
        unit: c.unit,
        color: c.color,
        type: "line",
      })),
    [binding.channels],
  );

  return (
    <div className="relative w-full h-full">
      <SeriesChart
        title={title}
        seriesDefs={seriesDefs}
        data={series}
        height={height}
        canDetail={binding.source.mode !== "nats"}
        onDetail={loadDetail}
        commentType={`panel_${binding.channels.map((c) => c.key).join("_")}`}
        machineBK={machineBK}
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default TimeSeriesPanel;
